import { Avatar, Badge, Card, CardContent, Progress } from '@bs/ui-components'
import { ArrowRight, Check } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { AvailabilityCard } from '@/features/profile/components/availability-card'
import { type ProfileChecklistItem, type ProfileChecklistId } from '@/features/profile/types'
import { useAccount } from '@/app/use-account'
import { type TalentAccount } from '@/services/account'
import { getMyProfile, type TalentProfile } from '@/features/profile/api'
import { getRecommendedJobs, type JobSearchItem } from '@/features/jobs/api'

/**
 * Real checklist, built from the same fields the rest of the app already treats as the source of
 * truth (`account.title` for headline, `profile.aboutMe` for summary, etc).
 */
/**
 * Exported so the profile HEADER and this panel compute completion the same way.
 *
 * They did not: the header read `completion` off the mock provider while this panel computed its
 * own, so one screen showed "Profile strength 100%" beside "Profile completion 50%". One concept,
 * one number, one function.
 */
/** The single definition of "how complete is this profile". */
export function completionFromChecklist(checklist: ProfileChecklistItem[]): number {
  if (!checklist.length) return 0
  return Math.round((checklist.filter((item) => item.checked).length / checklist.length) * 100)
}

export function computeRealChecklist(account: TalentAccount, profile: TalentProfile): ProfileChecklistItem[] {
  return [
    { id: 'registered', checked: Boolean(account.name.first?.trim() && account.email.trim()), text: 'Register with Brightsource' },
    { id: 'resume', checked: profile.cvs.length > 0, text: 'Add resume', subText: "Show employers you're qualified" },
    { id: 'education', checked: profile.education.length > 0, text: 'Add education', subText: 'List your degrees and qualifications' },
    { id: 'experience', checked: profile.employment.length > 0, text: 'Add work experience', subText: 'Tell employers about your previous roles' },
    { id: 'phone', checked: Boolean(account.phone?.trim()), text: 'Add phone number', subText: 'Hiring managers need to reach you!' },
    { id: 'skills', checked: profile.skills.length > 0, text: 'Add skills', subText: 'Tell employers why you stand out' },
    { id: 'headline', checked: Boolean(account.title?.trim()), text: 'Add headline', subText: 'Briefly summarize your experience' },
    { id: 'summary', checked: Boolean(profile.aboutMe?.trim()), text: 'Add summary', subText: 'Allow others to speak for your work' },
  ]
}

export function ProfileAside({
  onChecklistAction,
  realProfile: realProfileProp,
}: {
  onChecklistAction?(id: ProfileChecklistId): void
  /**
   * Pass the page's own live `realProfile` when one exists — `profile-page.tsx` does, so the
   * checklist reacts to every save (a skill added, a resume uploaded) immediately instead of
   * waiting for a reload to re-run this component's OWN one-time fetch. `account` comes from the
   * shared `AccountProvider` for the same reason — both used to be a private fetch here, frozen
   * from the moment this aside first mounted. The other pages that render this aside (jobs,
   * job detail) don't edit a profile, so they're fine falling back to the one-time fetch below;
   * `undefined` (prop omitted) selects that fallback, `null` (still loading) does not.
   */
  realProfile?: TalentProfile | null
}) {
  const { account, error: accountError } = useAccount()
  const [fetchedProfile, setFetchedProfile] = useState<TalentProfile | null>(null)
  const [loadError, setLoadError] = useState('')

  useEffect(() => {
    if (realProfileProp !== undefined) return
    let cancelled = false
    getMyProfile()
      .then(({ profile }) => {
        if (!cancelled) setFetchedProfile(profile)
      })
      .catch(() => {
        // Surfaced, not swallowed — with no profile the checklist card silently disappears, which
        // reads as "nothing to do here" rather than as a failed load.
        if (!cancelled) setLoadError('Could not load your profile checklist — refresh the page to try again.')
      })
    return () => {
      cancelled = true
    }
  }, [realProfileProp])

  const effectiveProfile = realProfileProp !== undefined ? realProfileProp : fetchedProfile

  /**
   * The checklist, computed from the candidate's OWN record — empty until it loads.
   *
   * It used to fall back to the mock provider's checklist and completion, which is how this panel
   * came to read "Profile completion 50%" beside a header saying "Profile strength 100%": two
   * numbers for one concept, one of them from a fixture.
   */
  const profileChecklist = account && effectiveProfile ? computeRealChecklist(account, effectiveProfile) : []
  const completion = completionFromChecklist(profileChecklist)
  const sortedChecklist = [...profileChecklist].sort(
    (first, second) => Number(first.checked) - Number(second.checked),
  )
  const hasIncompleteItem = profileChecklist.some((item) => !item.checked)

  // The aside's own fetch failing, or the shared account fetch failing, both leave the checklist
  // unbuildable — either way say so instead of hiding the card.
  const checklistError = loadError || (profileChecklist.length === 0 ? accountError : '')

  return (
    <aside className="flex min-w-0 flex-col gap-3 lg:w-[400px] lg:min-w-[320px] lg:shrink-0">
      <AvailabilityCard />
      <SuggestedJobsCard />
      {checklistError ? (
        <Card>
          <CardContent className="p-4">
            <p role="alert" className="text-sm text-destructive">{checklistError}</p>
          </CardContent>
        </Card>
      ) : null}
      {hasIncompleteItem ? <Card>
        <CardContent className="p-4">
          <div className="flex items-start gap-3.5">
            <Avatar name="Hila" className="size-20 text-xl" />
            <div className="min-w-0 flex-1">
              <h2 className="text-[22px] font-normal leading-7 text-primary">Hey I’m Hila,</h2>
              <p className="mt-1 text-base text-muted-foreground">Your personal recruiter.</p>
              {completion < 100 ? (
                <p className="mt-2 text-sm leading-5 text-muted-foreground">
                  Your profile is almost complete. Finish it to unlock better job matches and stand out.
                </p>
              ) : null}
            </div>
          </div>

          <div className="mb-8 mt-5">
            <div className="mb-2 flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Profile completion</span>
              <strong className="text-accent">{completion}%</strong>
            </div>
            <Progress value={completion} />
          </div>

          <ul className="space-y-4">
            {sortedChecklist.map((item) => (
              <li key={item.id}>
                <ChecklistItem item={item} onAction={onChecklistAction} />
              </li>
            ))}
          </ul>
        </CardContent>
      </Card> : null}
    </aside>
  )
}

function ChecklistItem({
  item,
  onAction,
}: {
  item: ProfileChecklistItem
  onAction: ((id: ProfileChecklistId) => void) | undefined
}) {
  const content = (
    <>
      <span
        className={`flex size-[21px] shrink-0 items-center justify-center rounded-full border ${
          item.checked ? 'border-accent bg-accent text-white' : 'border-[#555] bg-white'
        }`}
      >
        {item.checked ? <Check className="size-3.5 stroke-[3]" /> : null}
      </span>
      <span className="min-w-0 flex-1 px-1">
        <span className={`block text-base font-normal text-[#555] ${item.checked ? 'line-through' : ''}`}>
          {item.text}
        </span>
        {!item.checked && item.subText ? (
          <span className="mt-1 block text-xs leading-4 text-[#8a8a8a]">{item.subText}</span>
        ) : null}
      </span>
      {item.checked ? (
        <Badge variant="success">Success</Badge>
      ) : (
        <ArrowRight className="size-4 shrink-0 text-[#c7c7c7]" />
      )}
    </>
  )
  const className = `flex w-full items-center gap-2 rounded-[10px] px-1 py-1 text-left transition ${
    item.checked ? '' : 'hover:bg-input'
  }`

  if (item.checked || item.id === 'registered') {
    return <div className={className}>{content}</div>
  }

  if (item.id === 'phone') {
    return <Link to="/account-settings?scrollTo=account-access-section" className={className}>{content}</Link>
  }

  return <button type="button" className={className} onClick={() => onAction?.(item.id)}>{content}</button>
}


/** Legacy DetailsPanel's suggested-jobs card: the top recommendations, one line each. */
function SuggestedJobsCard() {
  const [jobs, setJobs] = useState<JobSearchItem[] | null>(null)
  useEffect(() => {
    let cancelled = false
    getRecommendedJobs({ limit: 3 })
      .then(({ items }) => {
        if (!cancelled) setJobs(items)
      })
      .catch(() => {
        if (!cancelled) setJobs([])
      })
    return () => {
      cancelled = true
    }
  }, [])
  if (!jobs?.length) return null
  return (
    <Card>
      <CardContent className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-medium text-primary">Suggested jobs</h2>
          <Link to="/jobs?tab=recommended" className="text-sm text-accent hover:underline">See all</Link>
        </div>
        <ul className="space-y-2">
          {jobs.map((job) => (
            <li key={job._id}>
              <Link to={`/job/${job.slug}`} className="block rounded-lg border border-border px-3 py-2 hover:bg-[#f8fbff]">
                <span className="block truncate text-sm font-medium text-[#404040]">{job.title}</span>
              </Link>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}
